import { t } from '@lingui/core/macro'
import { Trans } from '@lingui/react/macro'
import React, { useCallback, useEffect, useId, useMemo, useState } from 'react'
import DynamicSearch from '~/components/dynamic_search/dynamic_search'
import ExtractionModal from '~/components/dynamic_table/components/ExtractionModal'
import Pagination from '~/components/dynamic_table/components/Pagination'
import VisibilityToggle from '~/components/dynamic_table/components/VisibilityToggle'
import { DynamicTableProvider, useDynamicTable } from '~/components/dynamic_table/context'
import { buildTableSearchParams, saveParamsIntoStorage } from '~/components/dynamic_table/helpers/browserState'

import clientSideSearch from '~/components/dynamic_table/helpers/clientSideSearch'
import { discoverColumns, visibleColumns as getVisibleColumns } from '~/components/dynamic_table/helpers/columns'
import { formatValue } from '~/components/dynamic_table/helpers/formatValue'
import usePolling from '~/components/dynamic_table/hooks/usePolling'
import useServerSideSearch from '~/components/dynamic_table/hooks/useServerSideSearch'

export default function DynamicTable({ ...props }) {
  return (
    <DynamicTableProvider {...props}>
      <DynamicTableWrapper />
    </DynamicTableProvider>
  )
}

function DynamicTableWrapper() {
  const { state, props } = useDynamicTable()
  const { exports, polling, stickyFilters, fixedQuery = {} } = props
  const {
    data,
    setData,
    query,
    setQuery,
    sort,
    setSort,
    page,
    setPage,
    columns,
    setColumns,
    loading,
    setLoading,
    meta,
    setMeta,
  } = state

  const [isMounted, setIsMounted] = useState(false)
  const [showExportModal, setShowExportModal] = useState(false)

  const defaultTableId = useId()
  const tableId = props.id || defaultTableId
  const searchUrl = useMemo(() => props.url, [props.url])
  const searchFields = useMemo(() => props.search?.fields, [props.search])
  const initialData = useMemo(() => props.data, [props.data])

  const hiddenQueryParams = useMemo(() => {
    const fieldNames = new Set((searchFields || []).map((f) => f.name))
    const initialQuery = props.search?.query || {}
    return Object.fromEntries(Object.entries(initialQuery).filter(([key]) => !fieldNames.has(key)))
  }, [searchFields, props.search])
  const isServerSide = useMemo(() => !!searchUrl, [searchUrl])
  const visibleColumns = useMemo(() => getVisibleColumns(columns), [columns])
  const allAvailableColumns = useMemo(() => discoverColumns(data, columns), [data, columns])

  const pageInfo = useMemo(() => {
    if (meta.total === null) {
      const from = Math.max(1, (meta.current_page - 1) * meta.per_page + 1)
      const to = Math.min(meta.current_page * meta.per_page, from + data.length - 1)
      return {
        from,
        to: to > from ? to : from,
        total: null,
      }
    } else {
      return {
        from: Math.max(1, (meta.current_page - 1) * meta.per_page + 1),
        to: Math.min(meta.current_page * meta.per_page, meta.total),
        total: meta.total,
      }
    }
  }, [meta, data])

  // This function is responsible for updating the browser session storage with the current state parameters.
  // biome-ignore lint/correctness/useExhaustiveDependencies: todo: re-evaluate dependencies
  const updateStickyFilters = useCallback(() => {
    if (!stickyFilters.enabled) {
      return
    } // Abort if sticky filters are disabled

    const [locationPath, urlParams] = (searchUrl || '').split('?', 2)
    const { searchParams } = buildTableSearchParams({
      locationPath,
      urlParams,
      query,
      sort,
      page,
      per_page: meta.per_page,
    })

    saveParamsIntoStorage({ locationPath, searchParams, ttl: stickyFilters.ttl })
  }, [query, sort, page, meta, stickyFilters])

  // ----------------------------------------
  // Client-side handlers
  // ----------------------------------------
  // biome-ignore lint/correctness/useExhaustiveDependencies: todo: re-evaluate dependencies
  const handleClientSideSearch = useCallback(() => {
    const { data: paged, meta: newMeta } = clientSideSearch({
      initialData,
      query,
      sort,
      page,
      per_page: meta.per_page,
      searchFields: props.search.fields,
    })

    setData(paged)
    setMeta({ ...meta, ...newMeta })
  }, [query, page, sort, initialData, meta])

  // ----------------------------------------
  // Server-side handlers
  // ----------------------------------------
  const { fetchData } = useServerSideSearch({ baseUrl: searchUrl, method: props.method })

  const handleServerSideSearch = useCallback(
    async (options = { loading: true }) => {
      const { loading: loaderEnabled } = options

      if (loaderEnabled) setLoading(true)

      try {
        const res = await fetchData({ query: { ...fixedQuery, ...query }, sort, page, perPage: meta.per_page, options })
        if (!res || res.aborted) return

        setData(res.data)
        setMeta(res.meta)
        updateStickyFilters()
      } catch (err) {
        console.error('Error fetching data:', err)
      } finally {
        if (loaderEnabled) setLoading(false)
      }
    },
    [fetchData, query, sort, page, meta, setData, setMeta, setLoading, updateStickyFilters, fixedQuery],
  )

  // This is the callback function that handles changes performed by the DynamicSearch component
  const handleQueryChange = useCallback(
    (newQuery, newSort, newPage) => {
      React.startTransition(() => {
        const mergedQuery = { ...hiddenQueryParams, ...newQuery }
        const queryChanged = JSON.stringify(mergedQuery) !== JSON.stringify(query)
        const pageToSet = queryChanged ? 1 : newPage || 1

        setQuery(mergedQuery)
        setPage(pageToSet)
        setSort(newSort || { sort: null, dir: 'asc' })
      })
    },
    [setQuery, setPage, setSort, query, hiddenQueryParams],
  )

  // This function handles sorting when a column in the table header is clicked.
  // biome-ignore lint/correctness/useExhaustiveDependencies: todo: re-evaluate dependencies
  const handleSort = useCallback((key, sortKey) => {
    if (!sortKey) {
      sortKey = key
    }

    setSort(({ sort, dir }) => ({
      sort: sortKey,
      dir: sort === sortKey && dir === 'asc' ? 'desc' : 'asc',
    }))
  }, [])

  // ----------------------------------------
  // Effects
  // ----------------------------------------
  // biome-ignore lint/correctness/useExhaustiveDependencies: todo: re-evaluate the isMounted logic
  useEffect(() => {
    if (!isMounted) {
      setIsMounted(true)
    }
  }, [])

  // This effect handles the initial data load when the component mounts.
  // biome-ignore lint/correctness/useExhaustiveDependencies: todo: re-evaluate dependencies
  useEffect(() => {
    if (!isServerSide) {
      handleClientSideSearch()
    }
  }, [props.data])

  // This effect handles actions when the query, page, or sort state changes.
  // biome-ignore lint/correctness/useExhaustiveDependencies: todo: re-evaluate dependencies
  useEffect(() => {
    if (!isMounted || loading) {
      return
    }

    if (isServerSide) {
      handleServerSideSearch()
    } else {
      handleClientSideSearch()
    }
  }, [query, page, sort, isMounted])

  // This effect is necessary to set the columns state when the component mounts or when the columns prop changes.
  // biome-ignore lint/correctness/useExhaustiveDependencies: todo: re-evaluate dependencies
  useEffect(() => {
    setColumns((prevColumns) => {
      return prevColumns.map((column) => {
        const newColumn = props.columns.find(({ key }) => key === column.key)

        return {
          ...column,
          ...newColumn,
          visible: column.visible,
        }
      })
    })
  }, [props.columns])

  // This effect is responsible for performing polling (if enabled).
  usePolling(
    () => {
      if (!isServerSide) {
        return
      }

      handleServerSideSearch({ loading: false })
    },
    polling.interval,
    polling.enabled,
  )

  // ----------------------------------------
  // RENDERERS
  // ----------------------------------------
  const sortLinkClass = useCallback(
    (sortable, key, sortKey) => {
      const classes = ['text-decoration-none', 'text-reset']
      if (sortable) {
        classes.push('table-sort')
        if (sort.sort === (sortKey || key)) {
          classes.push(sort.dir)
        }
      }
      return classes.join(' ')
    },
    [sort],
  )

  const sortLabel = useMemo(() => {
    if (!sort.sort) {
      return null
    }

    const column = visibleColumns.find(({ key, sortKey }) => (sortKey || key) === sort.sort)
    return column?.label || sort.sort
  }, [sort.sort, visibleColumns])

  const cardBodyClass = useCallback(() => {
    const classes = ['card-body']
    const isEmpty = !props.visibilityToggle.enabled && searchFields?.length === 0 && exports.length === 0
    if (isEmpty) {
      classes.push('pt-0', 'pb-0')
    }
    return classes.join(' ')
  }, [props.visibilityToggle.enabled, searchFields, exports])

  return (
    <>
      <div className='dynamic-table card'>
        <div className={cardBodyClass()}>
          {searchFields.length > 0 && (
            <DynamicSearch
              fields={searchFields}
              onChange={handleQueryChange}
              sortOptions={{ ...sort, sortLabel }}
              pageOptions={page && page > 1 ? { page, per_page: meta.per_page } : {}}
              data-testid='dynamic-search'
            />
          )}

          <div className='d-flex justify-content-between' style={{ flexWrap: 'wrap' }}>
            {props.visibilityToggle.enabled && (
              <div className='pt-3'>
                <VisibilityToggle data-testid='column-toggle' />
              </div>
            )}

            <div className='d-flex justify-content-end gap-3'>
              {/* CSV export with column selection - only show for server-side tables */}
              {exports.includes('csv') && isServerSide && allAvailableColumns.length > 0 && (
                <button
                  type='button'
                  className='btn btn-secondary btn-sm mt-3'
                  onClick={() => setShowExportModal(true)}
                  data-testid='export-csv'
                >
                  <Trans>Export CSV</Trans>
                </button>
              )}
            </div>
          </div>
        </div>

        <div className='card-table table-responsive'>
          <table
            id={tableId}
            className='table table-vcenter card-table table-striped table-hover'
            data-testid='dynamic-table'
          >
            <thead>
              <tr>
                {visibleColumns.map(({ key, label, sortable, sortKey, header, tooltip }) => (
                  <th key={key}>
                    {header ? (
                      header()
                    ) : (
                      // biome-ignore lint/a11y/useValidAnchor: todo: fix this
                      <a
                        href='#'
                        className={sortLinkClass(sortable, key, sortKey)}
                        onClick={(e) => {
                          e.preventDefault()
                          e.stopPropagation()
                          sortable && handleSort(key, sortKey)
                        }}
                        style={{ cursor: sortable ? 'pointer' : 'default' }}
                      >
                        {label}
                        {tooltip && (
                          <span
                            data-controller='tooltip'
                            data-tooltip-title-value={tooltip}
                            style={{ marginLeft: '4px', cursor: 'help', verticalAlign: 'middle' }}
                          >
                            ⓘ
                          </span>
                        )}
                      </a>
                    )}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {!loading &&
                data.map((row, rowIndex) => (
                  // biome-ignore lint/suspicious/noArrayIndexKey: todo: fix this
                  <tr key={rowIndex}>
                    {visibleColumns.map(({ key, render }) => {
                      const cellValue = render ? render(row) : formatValue(row[key])
                      const isHtml = cellValue && typeof cellValue === 'object' && cellValue.__html !== undefined

                      // biome-ignore lint/security/noDangerouslySetInnerHtml: HTML is required in this case
                      return <td key={key}>{isHtml ? <span dangerouslySetInnerHTML={cellValue} /> : cellValue}</td>
                    })}
                  </tr>
                ))}
            </tbody>
          </table>
        </div>

        <div className='card-footer'>
          <div className='d-flex align-items-center'>
            {(data.length > 0 || meta.current_page > 1) && !loading ? (
              <>
                <p className='m-0 text-muted'>
                  {pageInfo.total !== null
                    ? t`Showing items ${pageInfo.from} to ${pageInfo.to} of ${pageInfo.total}`
                    : t`Showing items ${pageInfo.from} to ${pageInfo.to}`}
                </p>

                {(meta.total_pages === null || meta.total_pages > 1) && <Pagination data-testid='pagination' />}
              </>
            ) : (
              <p className='m-0 text-muted'>{loading ? t`Loading...` : t`No items found`}</p>
            )}
          </div>
        </div>
      </div>

      <ExtractionModal
        columns={allAvailableColumns}
        visibleColumns={visibleColumns.map((c) => c.key)}
        show={showExportModal}
        onClose={() => setShowExportModal(false)}
      />
    </>
  )
}
